import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import OtpVerification from '../components/OtpVerification';
import BrandLogo from '../components/BrandLogo';

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success(`Welcome back, ${data.user.name}!`);
      if (data.user.role === 'admin') navigate('/admin');
      else if (data.user.role === 'owner') navigate('/owner/dashboard');
      else navigate('/shops');
    } catch (err) {
      if (err.response?.data?.requiresOtp) {
        setOtpEmail(err.response.data.email || form.email);
        toast.info('OTP sent to your email');
        return;
      }
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-login-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1A1A1A 0%,#2D2D2D 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>

      {/* Back to Home */}
      <div className="auth-back" style={{ width: '100%', maxWidth: 440, marginBottom: 12 }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '7px 14px', color: '#9CA3AF', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          <ArrowLeft size={15} /> Back to Home
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: 20, padding: '36px 32px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} className="fade-in auth-card auth-login-card">
        {otpEmail ? (
          <OtpVerification
            email={otpEmail}
            title="Verify Email"
            subtitle="Your account needs email verification. Enter the OTP sent to your email."
            onVerified={(data) => {
              if (data.user.role === 'admin') navigate('/admin');
              else if (data.user.role === 'owner') navigate('/owner/dashboard');
              else navigate('/shops');
            }}
            onBack={() => setOtpEmail('')}
          />
        ) : (
        <>
        {/* Header */}
        <div className="auth-header" style={{ textAlign: 'center', marginBottom: 26 }}>
          <BrandLogo size={58} radius={16} />
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Welcome Back</h1>
          <p style={{ color: 'var(--gray)', fontSize: 14 }}>Sign in to your Material Stock account</p>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={15} color="var(--gray)" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input className="form-input" style={{ paddingLeft: 40 }} type="email" placeholder="you@example.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <Lock size={15} color="var(--gray)" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input className="form-input" style={{ paddingLeft: 40, paddingRight: 42 }} type={showPw ? 'text' : 'password'}
                placeholder="Enter your password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', display: 'flex' }}>
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '13px', fontSize: 15, marginTop: 4 }} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'right', marginTop: 9 }}>
          <Link to="/forgot-password" style={{ color: 'var(--orange)', fontWeight: 700, fontSize: 13 }}>Forgot password?</Link>
        </div>

        <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--gray)', marginTop: 18 }}>
          Don't have an account? <Link to="/register" style={{ color: 'var(--orange)', fontWeight: 600 }}>Create one</Link>
        </p>
        <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--gray)', marginTop: 6 }}>
          Are you a supplier? <Link to="/register/owner" style={{ color: 'var(--orange)', fontWeight: 600 }}>Register shop</Link>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
